import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft02Icon,
  Settings02Icon,
  Delete02Icon,
  ViewIcon,
  ViewOffSlashIcon,
  Cancel01Icon,
  Search01Icon,
  ArrowDown01Icon,
  ArrowUp02Icon,
  PlusSignIcon,
  Bookmark01Icon,
} from "@hugeicons/core-free-icons";

import { Content } from "@/types";
import { MenuSection } from "@/types/menus";
import { useAppSelector, useTerminology } from "@/hooks";
import { useUpdateContentMutation } from "@/features/organizations/api/contentApi";
import { getThumbnailImage } from "@/utils/media";
import Price from "@/components/Price";
import FirstSteps from "./FirstSteps";
import SectionSettingsForm from "./SectionSettingsForm";

interface Props {
  structure: MenuSection[];
  setStructure: React.Dispatch<React.SetStateAction<MenuSection[]>>;
  allContent: Content[];
  filteredProducts: Content[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  addSection: () => void;
  removeSection: (idx: number) => void;
  updateSection: (idx: number, patch: Partial<MenuSection>) => void;
  removeProductFromSection: (sectionIdx: number, productId: string) => void;
  isSaving: boolean;
  isPublished: boolean;
  setIsPublished: (val: boolean) => void;
  handleSave: () => Promise<void>;
  organizationId: string;
  menuId: string;
  onCreateProductClick: () => void;
  onImportProductClick: () => void;
}

type MobileView = "sections" | "product-selection" | "personalization";

const CatalogEditorMobile = ({
  structure,
  setStructure,
  allContent,
  addSection,
  removeSection,
  updateSection,
  removeProductFromSection,
  isSaving,
  isPublished,
  setIsPublished,
  handleSave,
  onImportProductClick,
}: Props) => {
  const { t } = useTerminology();
  const activeOrganization = useAppSelector((s) => s.ui.activeOrganization);
  const activeBranch = useAppSelector((s) => s.ui.activeBranch);
  const [updateContent] = useUpdateContentMutation();

  const [mobileView, setMobileView] = useState<MobileView>("sections");
  const [selectedSectionCategory, setSelectedSectionCategory] = useState<string | null>(null);

  // Local state for product checklist view
  const [localSearch, setLocalSearch] = useState("");
  const [checkedProductIds, setCheckedProductIds] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [isChecklistCollapsibleOpen, setIsChecklistCollapsibleOpen] = useState(false);

  // Retrieve current active section
  const activeSection = useMemo(() => {
    if (!selectedSectionCategory) return null;
    return structure.find((s) => s.category === selectedSectionCategory) || null;
  }, [structure, selectedSectionCategory]);

  const activeSectionIdx = useMemo(() => {
    if (!selectedSectionCategory) return -1;
    return structure.findIndex((s) => s.category === selectedSectionCategory);
  }, [structure, selectedSectionCategory]);

  // Sync checkboxes state when entering product selection
  useEffect(() => {
    if (mobileView === "product-selection" && activeSection) {
      setCheckedProductIds(activeSection.products ?? []);
      setLocalSearch("");
    }
  }, [mobileView, activeSection]);

  // Toggle checklist product id
  const handleToggleProductChecked = (productId: string) => {
    setCheckedProductIds((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Reordering sections helper
  const moveSectionUp = (idx: number) => {
    if (idx <= 0) return;
    setStructure((prev) => {
      const next = [...prev];
      const temp = next[idx];
      next[idx] = next[idx - 1];
      next[idx - 1] = temp;
      return next;
    });
  };

  const moveSectionDown = (idx: number) => {
    if (idx >= structure.length - 1) return;
    setStructure((prev) => {
      const next = [...prev];
      const temp = next[idx];
      next[idx] = next[idx + 1];
      next[idx + 1] = temp;
      return next;
    });
  };

  // Reordering products inside section helper
  const moveProductUp = (sectionIdx: number, pIdx: number) => {
    if (pIdx <= 0) return;
    setStructure((prev) =>
      prev.map((s, idx) => {
        if (idx !== sectionIdx) return s;
        const nextProducts = [...(s.products ?? [])];
        const temp = nextProducts[pIdx];
        nextProducts[pIdx] = nextProducts[pIdx - 1];
        nextProducts[pIdx - 1] = temp;
        return { ...s, products: nextProducts };
      })
    );
  };

  const moveProductDown = (sectionIdx: number, pIdx: number) => {
    const s = structure[sectionIdx];
    if (!s || !s.products || pIdx >= s.products.length - 1) return;
    setStructure((prev) =>
      prev.map((sec, idx) => {
        if (idx !== sectionIdx) return sec;
        const nextProducts = [...(sec.products ?? [])];
        const temp = nextProducts[pIdx];
        nextProducts[pIdx] = nextProducts[pIdx + 1];
        nextProducts[pIdx + 1] = temp;
        return { ...sec, products: nextProducts };
      })
    );
  };

  // Change product visibility
  const handleToggleProductVisibility = (prod: Content, isCurrentlyDisabled: boolean) => {
    if (!activeOrganization || !prod.id) return;
    updateContent({
      organizationId: activeOrganization,
      contentId: prod.id,
      content: {
        isDisabled: activeBranch ? Boolean(prod.isDisabled) : !isCurrentlyDisabled,
        ...(activeBranch && {
          branchSettings: {
            ...prod.branchSettings,
            [activeBranch]: {
              ...(prod.branchSettings as any)?.[activeBranch],
              isDisabled: !isCurrentlyDisabled,
            },
          },
        }),
      } as Content,
    });
  };

  // Build the list of products eligible for checklist
  // Shows products assigned to current section + completely unassigned products
  const availableProducts = useMemo(() => {
    const otherSectionsProducts = new Set<string>();
    structure.forEach((s) => {
      if (s.category !== selectedSectionCategory) {
        s.products?.forEach((id) => otherSectionsProducts.add(id));
      }
    });

    const searchStr = localSearch.toLowerCase().trim();
    return allContent.filter((c) => {
      if (otherSectionsProducts.has(c.id)) return false;
      if (!searchStr) return true;
      return c.title?.toLowerCase().includes(searchStr);
    });
  }, [allContent, structure, selectedSectionCategory, localSearch]);

  const handleSaveProductSelection = () => {
    if (activeSectionIdx !== -1) {
      updateSection(activeSectionIdx, { products: checkedProductIds });
    }
    setMobileView("sections");
  };

  // Checkbox toggle handler click on product selection card
  const handleChecklistRowClick = (productId: string) => {
    handleToggleProductChecked(productId);
  };

  // Toggle section accordion expand state
  const handleToggleSectionExpand = (category: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: prev[category] === false ? true : false,
    }));
  };

  // ── Render Views ───────────────────────────────────────────────────────────

  // 1. SECTIONS VIEW (Main editor screen on mobile)
  if (mobileView === "sections") {
    return (
      <Stack spacing={2} sx={{ height: "100%", overflowY: "auto", pb: 6, px: 1 }}>
        {/* Main Header Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f8ff 0%, #ffffff 100%)",
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h3" fontWeight={700} color="primary.main">
                Editor de Catálogo
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" fontWeight="bold">
                    Publicado
                  </Typography>
                }
                labelPlacement="start"
                sx={{ m: 0 }}
              />
            </Stack>
            <Stack direction="row" spacing={1.5}>
              <LoadingButton
                variant="contained"
                color="success"
                loading={isSaving}
                onClick={handleSave}
                fullWidth
                sx={{ borderRadius: 2, fontWeight: "bold", textTransform: "none", py: 1 }}
              >
                Guardar Cambios
              </LoadingButton>
            </Stack>
          </Stack>
        </Paper>

        {/* Collapsible Onboarding Checklist */}
        <Accordion
          expanded={isChecklistCollapsibleOpen}
          onChange={() => setIsChecklistCollapsibleOpen(!isChecklistCollapsibleOpen)}
          variant="outlined"
          sx={{
            borderRadius: "12px !important",
            border: "1px solid",
            borderColor: "divider",
            "&::before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={18} />}
            sx={{ px: 2, minHeight: 48 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HugeiconsIcon icon={Bookmark01Icon} size={20} color="#0052cc" />
              <Typography variant="subtitle2" fontWeight={600}>
                Guía de Inicio / Primeros pasos
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Divider />
            <FirstSteps structure={structure} onAddSection={addSection} />
          </AccordionDetails>
        </Accordion>

        {/* Sections list */}
        <Stack spacing={1.5}>
          <Typography variant="h4" fontWeight={700} sx={{ pl: 1 }}>
            Secciones ({structure.length})
          </Typography>

          {structure.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
              <Typography variant="body2" color="text.secondary">
                Aún no hay secciones. Crea una sección para empezar a organizar tus {t("product", 2)}.
              </Typography>
            </Paper>
          ) : (
            structure.map((section, sIdx) => {
              const isSectionExpanded = expandedSections[section.category] !== false;
              const sectionProducts = (section.products ?? [])
                .map((id) => allContent.find((c) => c.id === id))
                .filter(Boolean) as Content[];

              return (
                <Accordion
                  key={section.category}
                  expanded={isSectionExpanded}
                  onChange={() => handleToggleSectionExpand(section.category)}
                  variant="outlined"
                  sx={{
                    borderRadius: "12px !important",
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                    "&::before": { display: "none" },
                  }}
                >
                  {/* Section Summary Row */}
                  <AccordionSummary
                    expandIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={18} />}
                    sx={{
                      px: 2,
                      bgcolor: "action.hover",
                      "&.Mui-expanded": { borderBottom: "1px solid", borderColor: "divider" },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      flex={1}
                      minWidth={0}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Section Title */}
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        noWrap
                        sx={{
                          flex: 1,
                          textDecoration: section.isVisible === false ? "line-through" : "none",
                          color: section.isVisible === false ? "text.disabled" : "text.primary",
                        }}
                      >
                        {section.categoryTitle || "Sin título"}
                      </Typography>

                      {/* Reorder Arrows */}
                      <IconButton
                        size="small"
                        disabled={sIdx === 0}
                        onClick={() => moveSectionUp(sIdx)}
                        color="primary"
                        sx={{ p: 0.5 }}
                      >
                        <HugeiconsIcon icon={ArrowUp02Icon} size={16} />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={sIdx === structure.length - 1}
                        onClick={() => moveSectionDown(sIdx)}
                        color="primary"
                        sx={{ p: 0.5 }}
                      >
                        <HugeiconsIcon icon={ArrowDown01Icon} size={16} />
                      </IconButton>

                      {/* Toggle Visibility */}
                      <IconButton
                        size="small"
                        onClick={() => updateSection(sIdx, { isVisible: section.isVisible === false })}
                        sx={{ p: 0.5 }}
                      >
                        <HugeiconsIcon
                          icon={section.isVisible === false ? ViewOffSlashIcon : ViewIcon}
                          size={18}
                        />
                      </IconButton>

                      {/* Settings Gear */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSectionCategory(section.category);
                          setMobileView("personalization");
                        }}
                        color="primary"
                        sx={{ p: 0.5 }}
                      >
                        <HugeiconsIcon icon={Settings02Icon} size={18} />
                      </IconButton>

                      {/* Trash/Delete Section */}
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (window.confirm("¿Seguro que deseas eliminar esta sección?")) {
                            removeSection(sIdx);
                          }
                        }}
                        color="error"
                        sx={{ p: 0.5 }}
                      >
                        <HugeiconsIcon icon={Delete02Icon} size={18} />
                      </IconButton>
                    </Stack>
                  </AccordionSummary>

                  {/* Section Details - assigned products list */}
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      {sectionProducts.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ py: 2 }}>
                          No hay {t("product", 2)} en esta sección.
                        </Typography>
                      ) : (
                        sectionProducts.map((prod, pIdx) => {
                          const isProdDisabled =
                            Boolean(prod.isDisabled) ||
                            Boolean(activeBranch && (prod.branchSettings as any)?.[activeBranch]?.isDisabled);

                          return (
                            <Card
                              key={prod.id}
                              variant="outlined"
                              sx={{
                                opacity: isProdDisabled ? 0.6 : 1,
                                bgcolor: isProdDisabled ? "action.hover" : "background.paper",
                                borderRadius: 2,
                              }}
                            >
                              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                  {prod.images?.[0] ? (
                                    <CardMedia
                                      component="img"
                                      image={getThumbnailImage({
                                        file: prod.images,
                                        organizationId: activeOrganization,
                                        size: "150x150",
                                      })}
                                      sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: "cover" }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 1.5,
                                        bgcolor: "action.hover",
                                      }}
                                    />
                                  )}

                                  <Stack flex={1} minWidth={0}>
                                    <Typography variant="body2" fontWeight={600} noWrap>
                                      {prod.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      <Price currency="NIO">{prod.price || prod.startingPrice || 0}</Price>
                                    </Typography>
                                  </Stack>

                                  {/* Sort Arrows */}
                                  <IconButton
                                    size="small"
                                    disabled={pIdx === 0}
                                    onClick={() => moveProductUp(sIdx, pIdx)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <HugeiconsIcon icon={ArrowUp02Icon} size={14} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    disabled={pIdx === sectionProducts.length - 1}
                                    onClick={() => moveProductDown(sIdx, pIdx)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <HugeiconsIcon icon={ArrowDown01Icon} size={14} />
                                  </IconButton>

                                  {/* Product Visibility */}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleProductVisibility(prod, isProdDisabled)}
                                    sx={{ p: 0.5 }}
                                  >
                                    <HugeiconsIcon
                                      icon={isProdDisabled ? ViewOffSlashIcon : ViewIcon}
                                      size={14}
                                    />
                                  </IconButton>

                                  {/* Remove from Section */}
                                  <IconButton
                                    size="small"
                                    onClick={() => removeProductFromSection(sIdx, prod.id!)}
                                    color="error"
                                    sx={{ p: 0.5 }}
                                  >
                                    <HugeiconsIcon icon={Cancel01Icon} size={14} />
                                  </IconButton>
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })
                      )}

                      {/* Add Product Button inside Section */}
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        fullWidth
                        onClick={() => {
                          setSelectedSectionCategory(section.category);
                          setMobileView("product-selection");
                        }}
                        startIcon={<HugeiconsIcon icon={PlusSignIcon} size={16} />}
                        sx={{ textTransform: "none", borderRadius: 2, mt: 1 }}
                      >
                        Agregar {t("product", { count: 2 })}
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Stack>

        {/* Create Section Trigger Button at bottom */}
        <Button
          variant="contained"
          color="primary"
          onClick={addSection}
          fullWidth
          size="large"
          sx={{ borderRadius: 2.5, fontWeight: "bold", textTransform: "none", py: 1.5 }}
        >
          Crear Sección
        </Button>
      </Stack>
    );
  }

  // 2. PRODUCT SELECTION VIEW (Checkbox flow to select products for a category)
  if (mobileView === "product-selection" && activeSection) {
    return (
      <Stack spacing={2} sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", px: 1 }}>
        {/* Navigation Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
          <IconButton onClick={() => setMobileView("sections")} color="primary">
            <HugeiconsIcon icon={ArrowLeft02Icon} size={24} />
          </IconButton>
          <Stack minWidth={0}>
            <Typography variant="h3" fontWeight={700} noWrap>
              Agregar a {activeSection.categoryTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selecciona los {t("product", 2)} a incluir
            </Typography>
          </Stack>
        </Stack>

        {/* Search Bar */}
        <TextField
          placeholder={`Buscar ${t("product")}...`}
          size="small"
          fullWidth
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HugeiconsIcon icon={Search01Icon} size={18} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        {/* Products List Scrollable Box */}
        <Box sx={{ flex: 1, overflowY: "auto", pb: 2 }}>
          {availableProducts.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
              <Typography variant="body2" color="text.secondary">
                {localSearch ? "Sin resultados" : `No hay ${t("product", 2)} disponibles`}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {availableProducts.map((prod) => {
                const isChecked = checkedProductIds.includes(prod.id!);

                return (
                  <Card
                    key={prod.id}
                    variant="outlined"
                    onClick={() => handleChecklistRowClick(prod.id!)}
                    sx={{
                      cursor: "pointer",
                      borderColor: isChecked ? "primary.main" : "divider",
                      bgcolor: isChecked ? "action.selected" : "background.paper",
                      borderRadius: 2,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleProductChecked(prod.id!)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        {prod.images?.[0] ? (
                          <CardMedia
                            component="img"
                            image={getThumbnailImage({
                              file: prod.images,
                              organizationId: activeOrganization,
                              size: "150x150",
                            })}
                            sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: "cover" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1.5,
                              bgcolor: "action.hover",
                            }}
                          />
                        )}
                        <Stack flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {prod.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            <Price currency="NIO">{prod.price || prod.startingPrice || 0}</Price>
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* Sticky Actions Bar at the bottom */}
        <Paper
          elevation={4}
          sx={{
            p: 2,
            mx: -1,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <GridContainer>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={() => {
                onImportProductClick();
              }}
              sx={{ py: 1.2, textTransform: "none", borderRadius: 2, fontWeight: "bold" }}
            >
              Importar con IA
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSaveProductSelection}
              sx={{ py: 1.2, textTransform: "none", borderRadius: 2, fontWeight: "bold" }}
            >
              Guardar ({checkedProductIds.length})
            </Button>
          </GridContainer>
        </Paper>
      </Stack>
    );
  }

  // 3. PERSONALIZATION VIEW (Section style and column options on mobile)
  if (mobileView === "personalization" && activeSection) {
    return (
      <Stack spacing={2} sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", px: 1 }}>
        {/* Navigation Header */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
          <IconButton onClick={() => setMobileView("sections")} color="primary">
            <HugeiconsIcon icon={ArrowLeft02Icon} size={24} />
          </IconButton>
          <Stack minWidth={0}>
            <Typography variant="h3" fontWeight={700} noWrap>
              Personalizar: {activeSection.categoryTitle}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ajusta diseño y estilo
            </Typography>
          </Stack>
        </Stack>

        {/* Configuration settings form body */}
        <Box sx={{ flex: 1, overflowY: "auto", pb: 3 }}>
          <SectionSettingsForm
            section={activeSection}
            onUpdateSection={(patch) => {
              if (activeSectionIdx !== -1) {
                updateSection(activeSectionIdx, patch);
              }
            }}
          />
        </Box>

        {/* Bottom Save Action Button */}
        <Paper
          elevation={4}
          sx={{
            p: 2,
            mx: -1,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setMobileView("sections")}
            sx={{ py: 1.5, textTransform: "none", borderRadius: 2.5, fontWeight: "bold" }}
          >
            Listo
          </Button>
        </Paper>
      </Stack>
    );
  }

  return null;
};

// Lightweight helper for two buttons side-by-side on mobile
const GridContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      {children}
    </Stack>
  );
};

export default CatalogEditorMobile;
